/**
 * A wrapper to handle the Modern pagination style, and infinite scroll.
 *
 * Usage:
 *  {%- if settings.pagination_style == "modern" or settings.pagination_infinite -%}
 *    <script src="{{ 'custom-pagination.js' | asset_url }}" defer="defer"></script>
 *  {%- endif -%}
 *
 *  {%- paginate thing by stuff -%}
 *    <custom-pagination data-infinite-scroll="{{ settings.pagination_infinite }}"
 *      data-pause-infinite-scroll="false">
 *      <ul>
 *        <li class="js-pagination-result">One</li>
 *        <li class="js-pagination-result">Two</li>
 *        <li class="js-pagination-result">Three</li>
 *      </ul>
 *      {%- if paginate.pages > 1 -%}
 *        {% render 'pagination', paginate: paginate, style: settings.pagination_style %}
 *      {%- endif -%}
 *    </custom-pagination>
 *  {%- endpaginate -%}
 */
if (!customElements.get('custom-pagination')) {
  class CustomPagination extends HTMLElement {
    constructor() {
      super();
      this.init();
      document.addEventListener('on:bfcache:load-restore', this.reload.bind(this));
    }

    disconnectedCallback() {
      this.destroy();
    }

    init() {
      const savedUrl = sessionStorage.getItem('plp_url');
      if (savedUrl === window.location.href) {
        const savedHtml = sessionStorage.getItem('plp_html');
        const savedScroll = sessionStorage.getItem('plp_scroll');
        const savedPaginationHtml = sessionStorage.getItem('plp_pagination_html');
        const resultsContainer = this.querySelector('.js-pagination-result')?.parentNode;
        const paginationContainer = this.querySelector('.js-pagination'); 

        if (savedHtml && savedScroll && resultsContainer) {
          resultsContainer.innerHTML = savedHtml;

          if (savedPaginationHtml) {
            if (savedPaginationHtml === 'REMOVED' && paginationContainer) {
              paginationContainer.remove();
            } else if (paginationContainer) {
              paginationContainer.innerHTML = savedPaginationHtml;
            }
          }

          setTimeout(() => {
            window.scrollTo(0, parseInt(savedScroll, 10));
          }, 0);

          sessionStorage.removeItem('plp_html');
          sessionStorage.removeItem('plp_scroll');
          sessionStorage.removeItem('plp_url');
          sessionStorage.removeItem('plp_pagination_html');
        }
      }

      this.infiniteScrollCount = 0;
      this.infiniteScroll = this.dataset.infiniteScroll === 'true';
      this.infiniteScrollStopAfter = parseInt(this.dataset.infiniteScrollStop) || 100;
      this.pagination = this.querySelector('.js-pagination');
      this.loadMoreButton = this.querySelector('.js-pagination-load-more');
      this.results = this.querySelectorAll('.js-pagination-result');
      this.controller = null;

      this.resultClickHandler = this.resultClickHandler || this.handleResultClick.bind(this);
      this.addEventListener('click', this.resultClickHandler);

      this.loadMoreClickHandler = this.loadMoreClickHandler || this.handleLoadMoreClick.bind(this);

      this.savePlpStateHandler = this.savePlpStateHandler || this.savePlpState.bind(this);
      this.bindSaveStateListeners();

      if (this.pagination && this.pagination.dataset.isMoreResults && this.results) {
        if (this.loadMoreButton) this.loadMoreButton.addEventListener('click', this.loadMoreClickHandler);

        if (this.infiniteScroll) {
          this.paginationObserver = new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                if (entry.isIntersecting) {
                  this.loadMore();
                  this.intersectingTimer = setInterval(this.loadMore.bind(this), 1000);
                } else if (this.intersectingTimer) {
                  clearInterval(this.intersectingTimer);
                }
              });
            },
            { rootMargin: `${window.innerHeight * 1.5}px 0px` }
          );

          this.paginationObserver.observe(this.pagination);
        }
      }
    }

    /**
     * Binds the save state listener to product links.
     */
    bindSaveStateListeners() {
      this.results.forEach(result => {
        const productLink = result.querySelector('a');
        if (productLink) {
          productLink.removeEventListener('click', this.savePlpStateHandler);
          productLink.addEventListener('click', this.savePlpStateHandler);
        }
      });
    }

    /**
     * Saves the current PLP state to sessionStorage.
     */
    savePlpState() {
      const resultsContainer = this.querySelector('.js-pagination-result')?.parentNode;
        if (resultsContainer) {
          sessionStorage.setItem('plp_html', resultsContainer.innerHTML);
          sessionStorage.setItem('plp_scroll', window.scrollY);
          sessionStorage.setItem('plp_url', window.location.href);

          if (this.pagination && this.pagination.isConnected) {
            sessionStorage.setItem('plp_pagination_html', this.pagination.innerHTML);
          } else {
            sessionStorage.setItem('plp_pagination_html', 'REMOVED');
          }
        }
    }

    destroy() {
      if (this.intersectingTimer) clearInterval(this.intersectingTimer);
      if (this.paginationObserver) this.paginationObserver.disconnect();
      if (this.loadMoreButton) this.loadMoreButton.removeEventListener('click', this.loadMoreClickHandler);
      this.removeEventListener('click', this.resultClickHandler);
      
      this.results.forEach(result => {
        const productLink = result.querySelector('a');
        if (productLink) {
            productLink.removeEventListener('click', this.savePlpStateHandler);
        }
      });

      this.loadMoreClickHandler = null;
    }

    reload() {
      this.destroy();
      this.init();
    }

    handleLoadMoreClick(evt) {
      evt.preventDefault();
      this.infiniteScrollCount = 0;
      this.loadMore();
    }

    handleResultClick(evt) {
      if (evt.target.closest('a') && evt.target.closest('.js-pagination-result') && this.controller) {
        this.controller.abort();
      }
    }

    async loadMore() {
      if (this.infiniteScrollCount > this.infiniteScrollStopAfter) {
        return;
      }
      this.infiniteScrollCount += 1;

      if (!this.isFetching && this.dataset.pauseInfiniteScroll === 'false') {
        this.isFetching = true;
        this.controller = new AbortController();

        try {
          if (!this.loadMoreButton) throw new Error("No 'Load more' button found.");

          this.loadMoreButton.classList.add('is-loading');
          this.loadMoreButton.setAttribute('aria-disabled', 'true');

          if (this.pagination.dataset.paginationStyle === 'traditional') {
            this.pagination.classList.add('is-loading');
          }

          const response = await fetch(this.loadMoreButton.href, {
            signal: this.controller.signal
          });

          if (!response.ok) throw new Error(response.status);
          if (this.dataset.pauseInfiniteScroll === 'true') {
            throw new Error('Infinite scroll was interrupted.');
          }

          const tmpl = document.createElement('template');
          tmpl.innerHTML = await response.text();

          let newResultsHtml = '';
          const lastResult = this.results[this.results.length - 1];
          tmpl.content.querySelectorAll('.js-pagination-result').forEach((result) => {
            newResultsHtml += result.outerHTML;
          });
          lastResult.insertAdjacentHTML('afterend', newResultsHtml);
          this.results = this.querySelectorAll('.js-pagination-result');
          
          this.bindSaveStateListeners();

          const newPagination = tmpl.content.querySelector('.js-pagination');
          if (newPagination.dataset.isMoreResults) {
            this.pagination.innerHTML = newPagination.innerHTML;

            if (this.loadMoreButton) {
              this.loadMoreButton.removeEventListener('click', this.loadMoreClickHandler);
              this.loadMoreButton = this.querySelector('.js-pagination-load-more');
              this.loadMoreButton.addEventListener('click', this.loadMoreClickHandler);
            }
          } else {
            this.pagination.remove();
            if (this.intersectingTimer) clearInterval(this.intersectingTimer);
            if (this.paginationObserver) this.paginationObserver.disconnect();
            document.querySelectorAll('.js-when-paginated-only').forEach((elem) => elem.remove());
          }
        } catch (error) {
          console.log('Unable to fetch next page of results', error);
          if (this.intersectingTimer) clearInterval(this.intersectingTimer);
        } finally {
          this.controller = null;

          if (this.loadMoreButton) {
            this.loadMoreButton.classList.remove('is-loading');
            this.loadMoreButton.removeAttribute('aria-disabled');
          }

          this.isFetching = false;

          if (this.pagination.dataset.paginationStyle === 'traditional') {
            this.pagination.classList.remove('is-loading');
          }
        }
      }
    }
  }

  customElements.define('custom-pagination', CustomPagination);
}